// frontend/src/pages/CamDoBrainWeeklyPlan.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import camdobrainService from '../services/camdobrainService';
import Loading from '../components/Loading';
import './CamDoBrainWeeklyPlan.css';

const CamDoBrainWeeklyPlan = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [plan, setPlan] = useState(location.state?.plan || null);
  const [loading, setLoading] = useState(!location.state?.plan);
  const [selectedDay, setSelectedDay] = useState(null);

  const getContentTypeIcon = (type) => {
    const icons = {
      'REEL': '🎬',
      'IMAGE': '🖼️',
      'CAROUSEL': '📸',
      'VIDEO': '🎥',
      'STORY': '📱',
    };
    return icons[type] || '📄';
  };

  const getContentTypeColor = (type) => {
    const colors = {
      'REEL': '#ec4899',
      'IMAGE': '#3b82f6',
      'CAROUSEL': '#10b981',
      'VIDEO': '#f59e0b',
      'STORY': '#8b5cf6',
    };
    return colors[type] || '#64748b';
  };

  const getDayName = (dateStr) => {
    const days = ['Domingo', 'Lunes', 'Martes', 'Miercoles', 'Jueves', 'Viernes', 'Sabado'];
    const date = new Date(dateStr);
    return days[date.getDay()];
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('es-CL', { day: 'numeric', month: 'short' });
  };

  if (loading) {
    return <Loading />;
  }

  if (!plan || !plan.weekly_plan || plan.weekly_plan.length === 0) {
    return (
      <div className="weekly-plan-page">
        <div className="empty-state">
          <div className="empty-icon">📅</div>
          <h2>No hay plan disponible</h2>
          <p>Genera un nuevo plan semanal desde CamDoBrain</p>
          <button onClick={() => navigate('/camdobrain')}>
            Ir a CamDoBrain
          </button>
        </div>
      </div>
    );
  }

  const selectedPost = selectedDay !== null ? plan.weekly_plan[selectedDay] : null;

  return (
    <div className="weekly-plan-page">
      {/* Header */}
      <div className="plan-header">
        <button className="back-btn" onClick={() => navigate('/camdobrain')}>
          ← Volver
        </button>
        <h1>
          <span className="calendar-icon">📅</span>
          Plan Semanal de Contenido
        </h1>
      </div>

      {/* Summary Stats */}
      <div className="plan-summary">
        <div className="summary-stat">
          <div className="stat-value">{plan.weekly_plan.length}</div>
          <div className="stat-label">Publicaciones</div>
        </div>
        <div className="summary-stat">
          <div className="stat-value">{plan.total_predicted_reach?.toLocaleString()}</div>
          <div className="stat-label">Alcance Total Estimado</div>
        </div>
        <div className="summary-stat">
          <div className="stat-value">{plan.avg_predicted_er?.toFixed(2)}%</div>
          <div className="stat-label">ER Promedio Esperado</div>
        </div>
        <div className="summary-stat content-mix">
          <div className="stat-label">Mix de Contenido</div>
          <div className="mix-badges">
            {plan.content_mix && Object.entries(plan.content_mix).map(([type, count]) => (
              <span
                key={type}
                className="mix-badge"
                style={{ backgroundColor: getContentTypeColor(type) }}
              >
                {getContentTypeIcon(type)} {count}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="plan-content">
        {/* Timeline */}
        <div className="plan-timeline">
          <h2>Timeline</h2>
          <div className="timeline-list">
            {plan.weekly_plan.map((post, index) => (
              <div
                key={index}
                className={`timeline-item ${selectedDay === index ? 'selected' : ''}`}
                onClick={() => setSelectedDay(index)}
              >
                <div className="timeline-date">
                  <div className="date-day">{getDayName(post.recommended_date)}</div>
                  <div className="date-num">{formatDate(post.recommended_date)}</div>
                  <div className="date-time">{post.recommended_time}</div>
                </div>
                <div className="timeline-content">
                  <div
                    className="content-type-badge"
                    style={{ backgroundColor: getContentTypeColor(post.content_type) }}
                  >
                    {getContentTypeIcon(post.content_type)} {post.content_type}
                  </div>
                  <h3>{post.creative_idea?.idea_title || 'Idea creativa'}</h3>
                  <p>{post.creative_idea?.concept?.substring(0, 100) || post.creative_idea?.substring?.(0, 100)}...</p>
                </div>
                <div className="timeline-metrics">
                  <div className="metric">
                    <span className="metric-value">{post.predicted_engagement_rate?.toFixed(1)}%</span>
                    <span className="metric-label">ER</span>
                  </div>
                  <div className="metric">
                    <span className="metric-value">{post.confidence_score}%</span>
                    <span className="metric-label">Confianza</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Detail Panel */}
        <div className="plan-detail">
          {selectedPost ? (
            <>
              <div className="detail-header">
                <h2>
                  <span style={{ color: getContentTypeColor(selectedPost.content_type) }}>
                    {getContentTypeIcon(selectedPost.content_type)}
                  </span>
                  {' '}
                  {getDayName(selectedPost.recommended_date)} - {selectedPost.content_type}
                </h2>
                {selectedPost.prediction_id && (
                  <button
                    className="view-full-btn"
                    onClick={() => navigate(`/camdobrain/recommendation/${selectedPost.prediction_id}`)}
                  >
                    Ver Completo →
                  </button>
                )}
              </div>

              {/* Idea Section */}
              <div className="detail-section">
                <h3>💡 Idea Creativa</h3>
                <div className="idea-card">
                  <h4>{selectedPost.creative_idea?.idea_title || 'Sin titulo'}</h4>
                  <p>{selectedPost.creative_idea?.concept || selectedPost.creative_idea}</p>
                  {selectedPost.creative_idea?.hook && (
                    <div className="hook-preview">
                      <strong>Hook:</strong> {selectedPost.creative_idea.hook}
                    </div>
                  )}
                </div>
              </div>

              {/* Copy Preview */}
              <div className="detail-section">
                <h3>✍️ Copy</h3>
                <div className="copy-preview">
                  {selectedPost.hook && (
                    <p className="hook-line">{selectedPost.hook}</p>
                  )}
                  <p>{selectedPost.copy?.substring(0, 200) || selectedPost.copy_text?.substring(0, 200)}...</p>
                </div>
              </div>

              {/* Timing */}
              <div className="detail-section">
                <h3>📅 Cuando Publicar</h3>
                <div className="timing-info">
                  <div className="timing-item">
                    <span className="timing-icon">📆</span>
                    <span>{selectedPost.recommended_day_name || getDayName(selectedPost.recommended_date)}</span>
                  </div>
                  <div className="timing-item">
                    <span className="timing-icon">📅</span>
                    <span>{selectedPost.recommended_date}</span>
                  </div>
                  <div className="timing-item">
                    <span className="timing-icon">⏰</span>
                    <span>{selectedPost.recommended_time}</span>
                  </div>
                </div>
              </div>

              {/* Predictions */}
              <div className="detail-section">
                <h3>🎯 Performance Esperado</h3>
                <div className="predictions-grid">
                  <div className="prediction-box main">
                    <div className="pred-value">{selectedPost.predicted_engagement_rate?.toFixed(2)}%</div>
                    <div className="pred-label">Engagement Rate</div>
                  </div>
                  <div className="prediction-box">
                    <div className="pred-value">{selectedPost.predicted_reach?.toLocaleString()}</div>
                    <div className="pred-label">Alcance</div>
                  </div>
                  <div className="prediction-box">
                    <div className="pred-value">{selectedPost.predicted_likes?.toLocaleString()}</div>
                    <div className="pred-label">Likes</div>
                  </div>
                  <div className="prediction-box">
                    <div className="pred-value">{selectedPost.predicted_comments?.toLocaleString()}</div>
                    <div className="pred-label">Comentarios</div>
                  </div>
                </div>
              </div>

              {/* Hashtags Preview */}
              {selectedPost.hashtags && selectedPost.hashtags.length > 0 && (
                <div className="detail-section">
                  <h3>#️⃣ Hashtags</h3>
                  <div className="hashtags-preview">
                    {selectedPost.hashtags.slice(0, 10).map((tag, i) => (
                      <span key={i} className="hashtag">{tag}</span>
                    ))}
                    {selectedPost.hashtags.length > 10 && (
                      <span className="more-tags">+{selectedPost.hashtags.length - 10} mas</span>
                    )}
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="no-selection">
              <div className="no-selection-icon">👈</div>
              <h3>Selecciona un dia</h3>
              <p>Haz clic en cualquier publicacion del timeline para ver sus detalles</p>
            </div>
          )}
        </div>
      </div>

      {/* Actions */}
      <div className="plan-actions">
        <button className="action-btn secondary" onClick={() => navigate('/camdobrain')}>
          Volver a CamDoBrain
        </button>
        <button className="action-btn primary">
          ✓ Aprobar Plan Completo
        </button>
      </div>
    </div>
  );
};

export default CamDoBrainWeeklyPlan;
